<template>
  <div class="slider-root">
    <div class="slider-container">
      <input
        type="range"
        class="slider"
        :min="min"
        :max="max"
        :step="step"
        :value="value"
        :id="id"
        ref="slider-input"
        :style="sliderBackground"
        @input="displayValue($event)"
        @change="displayValue($event)"
        :aria-valuemin="min"
        :aria-valuemax="max"
        :aria-valuenow="value"
        :aria-valuetext="screenReaderText"
        :aria-labelledby="sliderLabel" />
      <div class="slider-ticks">
        <span v-for="value in values" :key="value.key" class="tick"> </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from '@vue/runtime-core';
import { ClientConstants, Duration } from './../client.constants';

export interface Values {
  key: number;
  leftValue: string | Duration;
  rightValue?: string | Duration;
}

export type Options = Array<Values>;

export default defineComponent({
  name: 'SliderComponent',
  props: {
    id: { type: String, required: true },
    min: { type: Number, default: 0, required: false },
    max: { type: Number, default: 5, required: false },
    step: { type: Number, default: 1, required: false },
    value: { type: Number, default: 0, required: false },
    values: {
      type: Array as PropType<Options>,
      required: true,
      validator(obj: { key: number; leftValue: string | Duration; rightValue: string | Duration }) {
        return obj.key !== null && obj.leftValue !== null && obj.rightValue !== null;
      }
    }
  },
  data: () => {
    return {
      clientConstants: ClientConstants
    };
  },
  computed: {
    /*
     * checks for the value in the values array passed in from the parent
     * returns the value if found, empty string if not
     */
    selectedValue(): string[] {
      const pair = this.values.find(obj => obj.key === this.value);
      if (pair) {
        return pair.rightValue ? [pair.leftValue, pair.rightValue] : [pair.leftValue, ''];
      }
      return ['', ''];
    },
    sliderLabel(): string {
      return `${this.id}-label`;
    },
    /*
     * uses the slider value to determine the percentage of the slider bar to
     * be filled, then uses this to create a gradient background.
     */
    sliderBackground(): { background: string } {
      const value = ((this.value - this.min) / (this.max - this.min)) * 100;
      return {
        background:
          'linear-gradient(to right, #0D6F3F 0%, #0D6F3F ' + value + '%, #3687274F ' + value + '%, #3687274F 100%)'
      };
    },
    /*
     * gives the string to be read for accessibility voiceover
     */
    screenReaderText(): string {
      return `Value selected: ${this.selectedValue[0]}, ${this.selectedValue[1]}`;
    }
  },
  methods: {
    /*
     * when input is changed, this function is called so the parent component
     * can handle the emitted value
     */
    displayValue(event: Event): void {
      this.$emit('sliderValueChanged', (event.target as HTMLInputElement).value);
    },
    validateGreaterThanZero(value: number): boolean {
      if (value > 0) {
        return true;
      }
      console.error(this.clientConstants.INVALID_SLIDER_GREATER_THAN_ZERO_MSG);
      return false;
    },
    validateGreaterThanOrEqualZero(value: number): boolean {
      if (value >= 0) {
        return true;
      }
      console.error(this.clientConstants.INVALID_SLIDER_GREATER_THAN_OR_EQUAL_ZERO_MSG);
      return false;
    },
    validateProps(): void {
      this.validateGreaterThanOrEqualZero(this.min);
      this.validateGreaterThanZero(this.max);
      this.validateGreaterThanZero(this.step);
      this.validateGreaterThanOrEqualZero(this.value);

      if (this.min >= this.max) {
        console.error(this.clientConstants.INVALID_SLIDER_MIN_VALUE_MSG);
        return;
      }
      if (this.step > this.max || this.step < 1) {
        console.error(this.clientConstants.INVALID_SLIDER_STEP_VALUE_MSG);
        return;
      }
      if (this.value > this.max || this.value < this.min) {
        console.error(this.clientConstants.INVALID_SLIDER_VALUE_MSG);
      }
    }
  },
  watch: {
    $props: {
      immediate: true,
      handler() {
        this.validateProps();
      }
    }
  }
});
</script>

<style scoped lang="scss">
* {
  box-sizing: border-box;
}

.slider-container {
  width: 100%;
  height: 30px;
  text-align: start;
}

.slider {
  -webkit-appearance: none;
  width: 100%;
  height: 5px;
  border-radius: 5px;
  outline: none;
  -webkit-transition: 0.2s;
  transition: opacity 0.2s;
  position: relative;
  z-index: 10;
}

.slider:hover {
  opacity: 1;
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #368727;
  cursor: pointer;
}

.slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #0d6f3f;
  cursor: pointer;
}

.slider-ticks {
  display: flex;
  justify-content: space-between;
  padding: 0 8px;
  margin: -21px 2px !important;
  width: 100%;
  position: relative;
  z-index: 0;
}

// Firefox
@supports (-moz-appearance: none) {
  .slider-ticks {
    margin: -23px 1.5px !important;
  }
}

.slider-ticks span {
  position: relative;
  display: flex;
  justify-content: center;
  text-align: center;
  width: 1px;
  background: #7f7f7f;
  height: 25px;
}
</style>
